// Turnstile (anti-robots de Cloudflare) pour les formulaires de contact et de devis.
// La clé de site vient du worker (GET /api/turnstile, variable TURNSTILE_SITEKEY) : vide = pas de Turnstile.
// Le widget se charge quand le visiteur commence à remplir un formulaire, reste caché sauf s'il faut cocher
// la case, et donne un jeton à chaque envoi. Si quelque chose ne charge pas, la demande part quand même :
// c'est le collecteur Pharos Leads qui décide.

interface TurnstileApi {
  render(el: HTMLElement, options: Record<string, unknown>): string
  reset(id: string): void
}

let loading: Promise<TurnstileApi | null> | null = null
let api: TurnstileApi | null = null
let widgetId = ''
let token = ''
const waiting: ((t: string) => void)[] = []

function give(t: string) {
  const next = waiting.shift()
  if (next) { next(t); if (t) again(); return }
  token = t
}

function again() {
  token = ''
  try { if (api) api.reset(widgetId) } catch { /* rien */ }
}

function loadScript(): Promise<TurnstileApi | null> {
  return new Promise(resolve => {
    const w = window as unknown as { turnstile?: TurnstileApi; __cogeTurnstile?: () => void }
    w.__cogeTurnstile = () => resolve(w.turnstile ?? null)
    const s = document.createElement('script')
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__cogeTurnstile'
    s.async = true
    s.onerror = () => resolve(null)
    document.head.appendChild(s)
    setTimeout(() => resolve(w.turnstile ?? null), 10000)
  })
}

export function prepareTurnstile(): Promise<TurnstileApi | null> {
  if (import.meta.env.DEV) return Promise.resolve(null)
  loading ??= fetch('/api/turnstile')
    .then(r => r.ok ? r.json() as Promise<{ sitekey?: string }> : {})
    .then(async ({ sitekey }: { sitekey?: string }) => {
      if (!sitekey) return null
      const t = await loadScript()
      if (!t) return null
      const box = document.createElement('div')
      box.style.cssText = 'position:fixed;right:12px;bottom:12px;z-index:2147483647'
      document.body.appendChild(box)
      widgetId = t.render(box, {
        sitekey,
        appearance: 'interaction-only',
        'response-field': false,
        'refresh-expired': 'auto',
        callback: give,
        'expired-callback': () => { token = '' },
        'error-callback': () => give(''),
      })
      api = t
      return t
    })
    .catch(() => null)
  return loading
}

// Le jeton du visiteur ('' si Turnstile n'est pas là ou n'a pas répondu à temps).
export async function turnstileToken(timeout = 8000): Promise<string> {
  const t = await prepareTurnstile()
  if (!t) return ''
  if (token) { const v = token; again(); return v }
  return new Promise(resolve => {
    let done = false
    const finish = (v: string) => { if (!done) { done = true; resolve(v) } }
    waiting.push(finish)
    setTimeout(() => { const i = waiting.indexOf(finish); if (i >= 0) waiting.splice(i, 1); finish('') }, timeout)
  })
}
